/**
 * Expand/collapse toggle for a delegation parent row in the list view.
 *
 * Renders a small chevron (▸ collapsed / ▾ expanded) that flips state
 * when clicked. Click is stopped from bubbling to the row's onClick so
 * toggling the tree doesn't also open the task detail.
 *
 * Spec: docs/specs/2026-04-16-session-state-audit.md §3.5.
 */

import { useCallback } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";

interface ListViewTreeToggleProps {
  expanded: boolean;
  onToggle: () => void;
  childCount: number;
  className?: string;
}

export function ListViewTreeToggle({
  expanded,
  onToggle,
  childCount,
  className,
}: ListViewTreeToggleProps) {
  const t = useT(framesTaskboardMessages);
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onToggle();
    },
    [onToggle],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-expanded={expanded}
      aria-label={t(expanded ? "collapseSubtasks" : "expandSubtasks", {
        count: childCount,
      })}
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded",
        "text-muted transition-colors hover:text-secondary",
        "focus:outline-none focus:ring-1",
        className,
      )}
      data-testid="listview-tree-toggle"
    >
      <Icons.ChevronRight
        className={cn("h-3 w-3 transition-transform", expanded && "rotate-90")}
      />
    </button>
  );
}
